// Minimal utilities to export SVG charts and datasets
export async function downloadPNGFromSVGContainer(container: HTMLElement, filename: string) {
  const svg = container.querySelector('svg.recharts-surface');
  if (!svg) throw new Error('No chart SVG found in container');
  const serializer = new XMLSerializer();
  const svgStr = serializer.serializeToString(svg);
  const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  // Draw SVG into canvas then download PNG
  const img = new Image();
  const bbox = svg.getBoundingClientRect();
  const scale = 2; // higher resolution
  const width = Math.max(1, Math.floor(bbox.width * scale));
  const height = Math.max(1, Math.floor(bbox.height * scale));

  return new Promise<void>((resolve, reject) => {
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('2D context not available');
        ctx.fillStyle = getComputedStyle(document.body).backgroundColor || '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error('Failed to create PNG blob'));
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = filename.endsWith('.png') ? filename : `${filename}.png`;
          a.click();
          URL.revokeObjectURL(a.href);
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

export function downloadCSV<T extends Record<string, any>>(rows: T[], filename: string) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(',')]
    .concat(rows.map(r => headers.map(h => safeCSV(String(r[h] ?? ''))).join(',')))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function safeCSV(value: string) {
  if (/[",\n]/.test(value)) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}
